import { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import api from '@/lib/api';
import { computePeriod, computeComparison } from '@/lib/periods';
import { useTenant } from '@/context/TenantContext';

const FilterContext = createContext(null);
export const useFilters = () => useContext(FilterContext);

export function FilterProvider({ children }) {
  const { current, isAll, ALL_CODE, association } = useTenant();
  const [options, setOptions] = useState(null);
  const [periodKey, setPeriodKey] = useState('all');
  const [customRange, setCustomRange] = useState({ start: null, end: null });
  const [dims, setDims] = useState({ campaign_codes: [], regions: [], lieux: [], responsables: [], canaux: [] });
  // Lentille transversale « famille de don » : null (tous) | 'regulier' | 'parrainage'.
  const [segment, setSegment] = useState(null);

  const parrainageOn = !!association?.parrainage?.enabled;
  const parrainageSeuil = association?.parrainage?.seuil || 30;

  useEffect(() => {
    if (!current) return;
    let active = true;
    setDims({ campaign_codes: [], regions: [], lieux: [], responsables: [], canaux: [] });
    setSegment(null); // on repart « tous les dons » à chaque changement d'association
    (async () => {
      try {
        const url = isAll ? '/admin/filters/options-all' : '/filters/options';
        const params = isAll ? undefined : { association: current };
        const { data } = await api.get(url, { params });
        if (active) setOptions(data);
      } catch (e) { /* ignore */ }
    })();
    return () => { active = false; };
  }, [current, isAll]);

  const period = useMemo(() => {
    if (periodKey === 'custom') return customRange;
    return computePeriod(periodKey, undefined, options?.date_min, options?.date_max);
  }, [periodKey, customRange, options]);

  // Calendar-aligned comparison window (WoW / MoM / YoY / MTD…) sent to the API
  // so KPI trends compare against the equivalent previous cycle.
  const comparePeriod = useMemo(
    () => computeComparison(periodKey, period),
    [periodKey, period]
  );

  const body = useMemo(() => ({
    association: isAll ? ALL_CODE : current,
    start: period.start,
    end: period.end,
    compare_start: comparePeriod.start,
    compare_end: comparePeriod.end,
    ...dims,
    parrainage_seuil: parrainageSeuil,
  }), [current, isAll, ALL_CODE, period, comparePeriod, dims, segment, parrainageOn, parrainageSeuil]);

  const setDim = useCallback((key, values) => {
    setDims((d) => ({ ...d, [key]: values }));
  }, []);

  const clearAll = useCallback(() => {
    setDims({ campaign_codes: [], regions: [], lieux: [], responsables: [], canaux: [] });
    setPeriodKey('all');
    setCustomRange({ start: null, end: null });
    setSegment(null);
  }, []);

  const activeCount = useMemo(() =>
    Object.values(dims).reduce((s, arr) => s + arr.length, 0) + (periodKey !== 'all' ? 1 : 0) + (segment ? 1 : 0),
    [dims, periodKey, segment]);

  return (
    <FilterContext.Provider value={{
      options, periodKey, setPeriodKey, customRange, setCustomRange,
      dims, setDim, period, comparePeriod, body, clearAll, activeCount,
      segment, setSegment, parrainageOn, parrainageSeuil,
    }}>
      {children}
    </FilterContext.Provider>
  );
}
